// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import AdminUser from 'AdminUser';
import FundOpportunity from '../../models/FundOpportunity';
import {
  CreateReviewRequestRequest,
  CreateReviewRequestResponse,
  GetReviewRequestsFilters,
  ReviewCommentsResponse,
  ReviewRequest,
  ReviewRequestListResponse,
  SubmitCommentRequest,
  SubmitCommentResponse,
  UpdateReviewStatusRequest,
  UpdateReviewStatusResponse,
} from '../../models/ReviewRequest';
import { NOT_FOUND } from '../../services/BaseBackendService';
export const adminBackendServiceReviewMixin = {
  async createReviewRequest(
    request: CreateReviewRequestRequest,
  ): Promise<CreateReviewRequestResponse> {
    return this.authApiMutateAuthed('/api/review-requests', 'POST', request, (json) => json, true);
  },

  async getReviewRequests(filters?: GetReviewRequestsFilters): Promise<ReviewRequestListResponse> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.status) params.append('status', filters.status);
      if (filters.requestedOf) params.append('requestedOf', 'true');
      if (filters.fundOpportunityId) params.append('fundOpportunityId', filters.fundOpportunityId);
      if (filters.type) params.append('type', filters.type);
    }
    const query = params.toString();
    return this.authApiGetAuthed(
      `/api/review-requests${query ? `?${query}` : ''}`,
      'Network error while fetching review requests',
      (json) => json as ReviewRequestListResponse,
      true,
    );
  },

  async getReviewRequest(externalId: string): Promise<ReviewRequest> {
    return this.authApiGetAuthed(
      `/api/review-requests/${externalId}`,
      'Network error while fetching review request',
      (json) => json.data as ReviewRequest,
      true,
    );
  },

  async updateReviewStatus(
    externalId: string,
    request: UpdateReviewStatusRequest,
  ): Promise<UpdateReviewStatusResponse> {
    return this.authApiMutateAuthed(
      `/api/review-requests/${externalId}/status`,
      'PUT',
      request,
      (json) => json as UpdateReviewStatusResponse,
      true,
    );
  },

  async submitComment(
    externalId: string,
    request: SubmitCommentRequest,
  ): Promise<SubmitCommentResponse> {
    return this.authApiMutateAuthed(
      `/api/review-requests/${externalId}/comments`,
      'POST',
      request,
      (json) => json as SubmitCommentResponse,
      true,
    );
  },

  async getReviewComments(externalId: string): Promise<ReviewCommentsResponse> {
    return this.authApiGetAuthed(
      `/api/review-requests/${externalId}/comments`,
      'Network error while fetching review comments',
      (json) => json as ReviewCommentsResponse,
      true,
    );
  },

  async getReviewData(reviewExternalId: string): Promise<any | null> {
    const token = await this.setupAssetStorageWithRetry(false);
    return this.getAsset(
      `contentReview/FundOpportunity/${reviewExternalId}/data.json`,
      token.token.token,
      { url: false },
    )
      .then((content) => (content ? JSON.parse(content as string) : null))
      .catch((error: any) => (error === NOT_FOUND ? null : Promise.reject(error)));
  },

  async getReviewDataUploadToken(reviewExternalId: string): Promise<{
    uploadUrl: string;
    storagePath: string;
  }> {
    return this.authApiMutateAuthed(
      '/api/generateAssetUploadUrl',
      'POST',
      {
        assetType: 'contentReview',
        modelId: reviewExternalId,
        modelType: 'FundOpportunity',
        fileName: 'data',
        fileExtension: 'json',
        contentType: 'application/json',
      },
      (data) => ({
        uploadUrl: data.uploadUrl,
        storagePath: `contentReview/FundOpportunity/${reviewExternalId}/data.json`,
      }),
      true,
    );
  },

  async uploadReviewData(reviewExternalId: string, data: any): Promise<void> {
    const { uploadUrl } = await this.getReviewDataUploadToken(reviewExternalId);
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }
  },

  async acceptSectionChanges(
    reviewExternalId: string,
    sectionId: string,
    acceptedBy: AdminUser,
    acceptanceComment?: string,
  ): Promise<void> {
    return this.authApiMutateAuthed(
      `/api/review-requests/${reviewExternalId}/accept-changes`,
      'POST',
      {
        sectionId,
        acceptedBy: acceptedBy.id,
        acceptanceComment,
      },
      () => undefined,
      true,
    );
  },

  async respondToComment(
    reviewExternalId: string,
    parentCommentId: string,
    response: string,
    submittedBy: AdminUser,
  ): Promise<void> {
    return this.authApiMutateAuthed(
      `/api/review-requests/${reviewExternalId}/respond-to-comment`,
      'POST',
      {
        parentCommentId,
        response,
        submittedBy: submittedBy.id,
      },
      () => undefined,
      true,
    );
  },

  async withdrawChangeSuggestion(
    reviewExternalId: string,
    sectionId: string,
    commentId: string,
    withdrawnBy: AdminUser,
    reason?: string,
  ): Promise<void> {
    return this.authApiMutateAuthed(
      `/api/review-requests/${reviewExternalId}/withdraw-suggestion`,
      'POST',
      {
        sectionId,
        commentId,
        withdrawnBy: withdrawnBy.id,
        reason,
      },
      () => undefined,
      true,
    );
  },

  async resubmitForReview(
    reviewExternalId: string,
    changes: any[],
    comments: any[],
  ): Promise<void> {
    return this.authApiMutateAuthed(
      `/api/review-requests/${reviewExternalId}/resubmit`,
      'POST',
      { changes, comments },
      () => undefined,
      true,
    );
  },
} satisfies ThisType<BaseBackendService>;
